import axios from 'axios';
import {
  HEADERS,
  type ApiSuccess,
  type AttachmentDone,
  type AttachmentStartInput,
  type AttachmentTicket,
  type CallRequestInput,
  type CsatInput,
  type ReasonPicker,
  type SupportLanguage,
  type TicketCreateInput,
  type TicketList,
  type TicketMessageInput,
  type TicketView,
} from '@capsico/contracts';
import { api } from './client';

/*
 * Help + chat (W16). The same requester API serves customers, partners and riders under their own
 * base path; tickets are always the signed-in person's own. Every POST carries an
 * X-Idempotency-Key (client default); opening a ticket passes a stable key so a retry after a
 * dropped connection never opens a second ticket.
 */

const BASE = '/v1/customer/me/support';

async function get<T>(path: string, params?: object): Promise<T> {
  const res = await api.get<ApiSuccess<T>>(`${BASE}${path}`, params ? { params } : undefined);
  return res.data.data;
}

async function post<T>(path: string, body: unknown = {}, key?: string): Promise<T> {
  const res = await api.post<ApiSuccess<T>>(
    `${BASE}${path}`,
    body,
    key ? { headers: { [HEADERS.IDEMPOTENCY_KEY]: key } } : undefined,
  );
  return res.data.data;
}

export const fetchReasons = (orderId?: string) =>
  get<ReasonPicker>('/reasons', orderId ? { orderId } : undefined);
export const fetchTickets = () => get<TicketList>('/tickets');
export const fetchTicket = (id: string) => get<TicketView>(`/tickets/${id}`);

/** Opens a ticket; `key` is the ticket's op id (reuse it when retrying the same submit). */
export const createTicket = (input: TicketCreateInput, key: string) =>
  post<TicketView>('/tickets', input, key);
export const postMessage = (id: string, input: TicketMessageInput) =>
  post<TicketView>(`/tickets/${id}/messages`, input);
export const askForAgent = (id: string) => post<TicketView>(`/tickets/${id}/agent`);
export const setTicketLanguage = (id: string, language: SupportLanguage) =>
  post<TicketView>(`/tickets/${id}/language`, { language });
export const rateTicket = (id: string, input: CsatInput) =>
  post<TicketView>(`/tickets/${id}/csat`, input);
export const requestCall = (id: string, input: CallRequestInput) =>
  post<TicketView>(`/tickets/${id}/call`, input);

/** Photo upload: ask for a short-lived PUT link, send the file straight to storage, confirm. */
export async function uploadSupportPhoto(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<AttachmentDone> {
  const start: AttachmentStartInput = {
    contentType: file.type as AttachmentStartInput['contentType'],
    sizeBytes: file.size,
  };
  const ticket = await post<AttachmentTicket>('/attachments', start);
  // Plain axios (not the API client): the upload goes to storage, not to the Capsico API.
  await axios.put(ticket.url, file, {
    headers: ticket.headers,
    timeout: 120_000,
    onUploadProgress: (event) => {
      if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });
  return post<AttachmentDone>(`/attachments/${ticket.attachmentId}/complete`);
}
